import React, { useContext, useEffect, useState } from 'react'
import { Table, Button, Dropdown, Typography, Affix, Select, Input, App } from 'antd'
import { isMobileOnly, isDesktop } from "react-device-detect"
import { useNavigate, Link } from "react-router-dom"
import _ from "lodash"
import { FaPlus, FaEllipsisH, FaSearch } from "react-icons/fa"
import { API, AntDesign, Enum } from "../../../resources/constants"
import language from "../../../resources/languages/fa_IR"

// Contexts
import AxiosContext from "../../../contexts/axios"

// Interfaces
import { AxiosError, AxiosResponsePaginate } from "../../../interfaces/axios"
import { AntTableParams } from "../../../interfaces/ant"
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { FilterValue, SorterResult } from 'antd/es/table/interface'

const { Pages: { BasicInformation: { City: CityLanguage } }, Enum: EnumLanguage, Share, Message: MessageLanguage } = language
const {  BASIC_INFORMATION_CITY_PAGINATE, BASIC_INFORMATION_CITY_REMOVE, BASIC_INFORMATION_PROVINCE_ALL } = API
const { Message } = Enum
const { theme: { token: { colorPrimary } } } = AntDesign

interface ProvinceType {
    id: string,
    name: string,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}
interface CityType {
    row: number,
    key: string,
    id: string,
    name: string,
    province: ProvinceType,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

export default function Index() {

    const navigate = useNavigate()
    const [cities, setCities] = useState<CityType[]>([])
    const [loadingRecords, setLoadingRecords] = useState<boolean>(false)
    const [loadingProvinces, setLoadingProvinces] = useState<boolean>(true)
    const [provinces, setProvinces] = useState<ProvinceType[]>([])
    const [province, setProvince] = useState<string>()
    const [name, setName] = useState<string>()
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        total: undefined,
        current: 1,
        pageSize: 10,
        showTotal: isMobileOnly ? undefined : (total: number): string => `${Share.paginationShowTotal} (${total})`,
        showSizeChanger: !isMobileOnly,
        showQuickJumper: !isMobileOnly,
    })
    const { axios } = useContext(AxiosContext)
    const { message, modal } = App.useApp()

    const columns: ColumnsType<CityType> = [
        {
            title: Share.row,
            dataIndex: "row",
            key: "row",
        },
        {
            title: CityLanguage.name,
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
                <div style={{padding: 8}}>
                    <Input
                        allowClear
                        placeholder={CityLanguage.name}
                        onChange={({ target: { value: name } }) => !name && setName(name)}
                        onBlur={({ target: { value: name } }) => setName(name)}
                    />
                </div>
            ),
            filterIcon: (filtered: boolean) => <FaSearch color={name && colorPrimary} />
        },
        {
            title: CityLanguage.province,
            dataIndex: ['province', 'name'],
            key: 'province',
            sorter: true,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
                <div style={{padding: 8}}>
                    <Select
                        showSearch
                        allowClear
                        style={{minWidth: 150}}
                        optionFilterProp="label"
                        placeholder={Share.pleaseSelectedRecord}
                        onChange={(province) => setProvince(province)}
                        options={provinces.map(({ id, name}) => ({ label: name, value: id}))}
                    />
                </div>
            ),
            filterIcon: (filtered: boolean) => <FaSearch color={province && colorPrimary} />
        },
        {
            title: Share.operation,
            dataIndex: "operation",
            key: "operation",
            render: (text, record) =>  <Dropdown menu={{ items: [
                    {
                        key: 'edit',
                        label: <Link to={'/basic-information/city/edit/' + record.id}>{ Share.edit }</Link>,
                    },
                    {
                        key: 'delete',
                        label: <Typography.Link onClick={() => modal.confirm({
                            title: Share.deleteConfirmTitle,
                            okText: Share.deleteConfirmOk,
                            okType: 'danger',
                            cancelText: Share.deleteConfirmCancel,
                            onOk() {
                                setLoadingRecords(true)
                                axios.delete(BASIC_INFORMATION_CITY_REMOVE + record.id).then((response: {}) => {
                                    const { total = 1, pageSize = 1, current = 1 } = pagination
                                    const params = {
                                        page: total % pageSize !== 1 ? current : current - 1,
                                        limit: pageSize,
                                    }
                                    getCities(params)
                                    message.success(MessageLanguage[Message.DELETE_RECORD]).then(response => response)
                                }).catch((error: AxiosError) => {
                                    const { response: { data } } = error
                                    // @ts-ignore
                                    message.error(MessageLanguage[data.message]).then(response => response )
                                    setLoadingRecords(false)
                                })
                            }
                        })}>{ Share.delete }</Typography.Link>,
                    },
                ] }} placement="bottomRight"><a><FaEllipsisH /></a></Dropdown>
        }
    ]

    const getCities = (params: AntTableParams) => {
        setLoadingRecords(true)
        axios.post(BASIC_INFORMATION_CITY_PAGINATE, { name, province }, { params }).then((response: AxiosResponsePaginate) => {
            const { data: { docs: cities, page: current, limit: pageSize, totalDocs: total } } = response
            let row = (current - 1) * pageSize
            setCities(cities.map<CityType>((city: CityType) => ({ ...city, row: ++row, key: city.id })))
            setLoadingRecords(false)
            setPagination({
                ...pagination,
                current,
                pageSize,
                total,
            })
        }).catch((error: AxiosError) => {
            const { response: { data } } = error
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
            setLoadingRecords(false)
        })
    }

    const getProvinces = async () => axios.post(BASIC_INFORMATION_PROVINCE_ALL).then((response: { data: ProvinceType[] }) => {
        const { data: provinces } = response
        setProvinces(provinces)
        setLoadingProvinces(false)
    }).catch((error: AxiosError) => {
        const { response: { data } } = error
        // @ts-ignore
        message.error(MessageLanguage[data.message]).then(response => response )
    })

    const initialAPI = async () => {
        await getProvinces()
    }

    useEffect(() => {
        initialAPI().then(response => response)
    }, [])

    useEffect(() => {
        const { current, pageSize } = pagination
        getCities({ page: current, limit: pageSize })
    }, [province, name])

    const handleTableChange = (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue|null>,
        sorter: SorterResult<any>|SorterResult<any>[]
    ): void => {
        const { current, pageSize } = pagination
        const sort = !_.isArray(sorter) && sorter.order && `${sorter.field},${sorter.order && sorter.order.slice(0, -3)}`
        getCities({ page: current, limit: pageSize, sort })
    }

    return (
        <>
            <Table
                columns={columns}
                dataSource={cities}
                pagination={pagination}
                loading={loadingRecords || loadingProvinces}
                onChange={handleTableChange}
                scroll={{ x: true }}
            />
            <Affix offsetBottom={30}>
                <Button type="primary" style={{marginTop: 10}} icon={<FaPlus />} onClick={() => navigate('/basic-information/city/add')}>{ Share.add }</Button>
            </Affix>
        </>
    )
}
